import React, { useState } from 'react';
import { Box } from '@mui/material';
import VideoPlayer from './VideoPlayer';
import InstructionTitle from './InstructionTitle';
import MiniBtn from './MiniBtn';
import { useAudio } from './AudioRef';

/**
 * PhonicsStory — 统一故事/词汇组件
 *
 * 统一数据结构：
 * {
 *   layout: '...',
 *   videos: [{ src, poster?, startTime?, endTime?, lines? }],
 *   lines: [{ en, cn?, audioEn, audioCn? }],
 *   splitAt?: number,
 *   storyLines?: [{ en, cn?, audioEn, audioCn? }],
 * }
 *
 * splitAt 切分规则：
 * - video-left-vocab-right: splitAt = N → lines[0..N-1] = 右侧单词，lines[N..] = 视频下方句子
 * - dual-videos: splitAt = N → lines[0..N-1] = 左视频台词，lines[N..] = 右视频台词
 * - vocab-stack-story-right: splitAt = N → 前 N 个视频 = vocab，各自带 video.lines
 *                                         storyLines 或 lines[N..] = story 视频下方句子
 * - vocab-story-swap: splitAt = N → 前 N 个视频 = vocab，各自带 video.lines
 *                                         storyLines 或 lines[N..] = story 视频下方句子
 * - story-video-left-lines-right: splitAt = N → lines[0..N-1] = 左视频下方，lines[N..] = 右边
 *
 * layout 类型：
 * - 'video-top-lines-bottom'        视频在上，台词在下
 * - 'video-left-vocab-right'        左视频 + 句子 / 右单词（splitAt = 单词数量）
 * - 'dual-videos'                   双视频并排，各配台词
 * - 'vocab-stack-story-right'       左2 vocab 上下叠 / 右 story 放大
 * - 'vocab-story-swap'              等宽互换：左 vocab / 右 story
 * - 'story-video-left-lines-right'  左视频+前N行 / 右剩余行
 * - 'text-left-words-right-phrases' 无视频，纯文字左右分栏
 * - 'vocab-dual'                    左右各一 vocab 组：图在上，图下单词+句子并排
 */

/** ======================= 通用渲染函数 ======================= */

/** 播放行（单语） */
function LinesMono({ lines, playingKey, onPlay, prefix = '' }) {
  return (lines || []).map((line, idx) => (
    <div className="phonics-lt-row" key={idx}>
      <span className="phonics-lt-en">{line.en}</span>
      <MiniBtn
        playing={playingKey === `${prefix}${idx}en`}
        onClick={() => onPlay(`${prefix}${idx}en`, line.audioEn)}
        title={line.en}
      />
    </div>
  ));
}

/** 播放行（双语：en + 可选 cn） */
function LinesBi({ lines, playingKey, onPlay, prefix = '' }) {
  return (lines || []).map((line, idx) => (
    <div className={`phonics-story-row${line.cnNewLine ? ' phonics-story-row-newline' : ''}`} key={idx}>
      <div className="phonics-story-cell-en">
        <span className="phonics-story-en">{line.en}</span>
        <MiniBtn
          playing={playingKey === `${prefix}${idx}en`}
          onClick={() => onPlay(`${prefix}${idx}en`, line.audioEn)}
          title={line.en}
        />
      </div>
      {line.cn && (
        <div className={`phonics-story-cell-cn${line.cnNewLine ? ' phonics-story-cn-newline' : ''}`}>
          <span className="phonics-story-cn">{line.cn}</span>
          <MiniBtn
            playing={playingKey === `${prefix}${idx}cn`}
            onClick={() => onPlay(`${prefix}${idx}cn`, line.audioCn)}
            title={line.cn}
          />
        </div>
      )}
    </div>
  ));
}

/** 单词行（双语） */
function WordRows({ lines, playingKey, onPlay, prefix = '', skipTopMargin = false }) {
  return (lines || []).map((line, idx) => (
    <div className={`phonics-vocab-word-row${idx === 0 && skipTopMargin ? ' phonics-vocab-word-row-first' : ''}${line.cnNewLine ? ' phonics-vocab-word-row-newline' : ''}`} key={idx}>
      {line.cnNewLine ? (
        <>
          <div className="phonics-vocab-en-line">
            <span className="phonics-vocab-word-en">{line.en}</span>
            <MiniBtn
              playing={playingKey === `${prefix}${idx}en`}
              onClick={() => onPlay(`${prefix}${idx}en`, line.audioEn)}
              title={line.en}
            />
          </div>
          {line.cn && (
            <div className="phonics-vocab-word-cn-newline">
              <span className="phonics-vocab-word-cn">{line.cn}</span>
              <MiniBtn
                playing={playingKey === `${prefix}${idx}cn`}
                onClick={() => onPlay(`${prefix}${idx}cn`, line.audioCn)}
                title={line.cn}
              />
            </div>
          )}
        </>
      ) : (
        <>
          <span className="phonics-vocab-word-en">{line.en}</span>
          <MiniBtn
            playing={playingKey === `${prefix}${idx}en`}
            onClick={() => onPlay(`${prefix}${idx}en`, line.audioEn)}
            title={line.en}
          />
          {line.cn && (
            <>
              <span className="phonics-vocab-word-cn">{line.cn}</span>
              <MiniBtn
                playing={playingKey === `${prefix}${idx}cn`}
                onClick={() => onPlay(`${prefix}${idx}cn`, line.audioCn)}
                title={line.cn}
              />
            </>
          )}
        </>
      )}
    </div>
  ));
}

/** 句子行（双语） */
function SentenceRows({ lines, playingKey, onPlay, prefix = '' }) {
  return (lines || []).map((line, idx) => (
    <div className="phonics-vocab-sentence-row" key={idx}>
      <span className="phonics-vocab-sentence-en">{line.en}</span>
      <MiniBtn
        playing={playingKey === `${prefix}${idx}en`}
        onClick={() => onPlay(`${prefix}${idx}en`, line.audioEn)}
        title={line.en}
      />
      {line.cn && (
        <>
          <span className="phonics-vocab-divider">|</span>
          <span className="phonics-vocab-sentence-cn">{line.cn}</span>
          <MiniBtn
            playing={playingKey === `${prefix}${idx}cn`}
            onClick={() => onPlay(`${prefix}${idx}cn`, line.audioCn)}
            title={line.cn}
          />
        </>
      )}
    </div>
  ));
}

/** 渲染视频组件 */
function VideoItem({ video, mb }) {
  if (!video) return null;
  // 检测 src 是否为图片格式（png/jpg/jpeg/gif/webp/svg）
  const isImage = /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(video.src);
  if (isImage) {
    return (
      <div className="phonics-vocab-video">
        <img
          src={video.src}
          alt=""
          style={{ width: '100%', borderRadius: 12, display: 'block' }}
        />
      </div>
    );
  }
  return (
    <div className="phonics-vocab-video">
      <VideoPlayer
        src={video.src}
        poster={video.poster}
        startTime={video.startTime}
        endTime={video.endTime}
      />
    </div>
  );
}

/** ======================= layout 实现 ======================= */

/** video-top-lines-bottom: 视频在上，台词在下 */
function VideoTopLinesBottom({ data, title, mb, audio }) {
  const [playingKey, setPlayingKey] = useState(null);

  const playAudio = (key, src) => {
    audio.play(src, () => setPlayingKey(null));
    setPlayingKey(key);
  };

  const { videos, lines } = data;
  const mainLines = videos[0]?.lines || lines;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, gap: 0.5 }}>
      <InstructionTitle
        title={title || data.label}
        titleCn="跟着说"
        instructionAudio={data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`}
        inlineCn
      />

      <div className="phonics-story-video-center">
        <VideoItem video={videos[0]} mb={mb} />
      </div>

      <div className="phonics-story-lines-table">
        <LinesBi lines={mainLines} playingKey={playingKey} onPlay={playAudio} />
      </div>
    </Box>
  );
}

/** video-left-vocab-right: 左 video + 句子 / 右 单词 */
function VideoLeftVocabRight({ data, title, mb, audio }) {
  const [playingKey, setPlayingKey] = useState(null);

  const playAudio = (key, src) => {
    audio.play(src, () => setPlayingKey(null));
    setPlayingKey(key);
  };

  const { videos, lines, splitAt } = data;
  const mainVideo = videos[0] || {};

  // splitAt 决定切分：
  //   lines[0..splitAt-1] = 右侧单词
  //   lines[splitAt..] = 视频下方句子
  const sa = splitAt !== undefined ? splitAt : 0;
  const wordLines = lines.slice(0, sa);
  // 视频下方句子：如果 video 自带 lines，用它；否则取 lines[splitAt..]
  const sentenceLines = mainVideo.lines || lines.slice(sa);

  return (
    <Box sx={{ width: '100%' }}>
      <InstructionTitle
        title={title || data.label}
        titleCn="跟着说"
        instructionAudio={data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`}
        inlineCn
      />

      <div className="phonics-vocab-layout">
        {/* 左侧：视频 + 句子 */}
        <div className="phonics-vocab-left">
          <VideoItem video={mainVideo} mb={mb} />
          {sentenceLines.length > 0 && (
            <div className="phonics-vocab-sentences">
              <SentenceRows lines={sentenceLines} playingKey={playingKey} onPlay={playAudio} prefix="sl" />
            </div>
          )}
        </div>

        {/* 右侧：单词 */}
        <div className="phonics-vocab-content">
          <WordRows lines={wordLines} playingKey={playingKey} onPlay={playAudio} prefix="w" />
        </div>
      </div>
    </Box>
  );
}

/** dual-videos: 双视频并排，各配台词 */
function DualVideos({ data, title, mb, audio }) {
  const [playingKey, setPlayingKey] = useState(null);

  const playAudio = (key, src) => {
    audio.play(src, () => setPlayingKey(null));
    setPlayingKey(key);
  };

  const { videos, lines = [] } = data;
  const leftVideo = videos[0];
  const rightVideo = videos[1];

  // 优先用各自 video 的 lines，否则用顶层 lines（根据 splitAt 切分）
  const leftLines = leftVideo?.lines?.length ? leftVideo.lines : (lines.slice(0, Math.ceil(lines.length / 2)) || []);
  const rightLines = rightVideo?.lines?.length ? rightVideo.lines : (lines.slice(Math.ceil(lines.length / 2)) || []);

  return (
    <Box sx={{ width: '100%' }}>
      <InstructionTitle
        title={title || data.label}
        titleCn="跟着说"
        instructionAudio={data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`}
        inlineCn
      />

      <div className="phonics-vocab-multi">
        <div className="phonics-vocab-multi-item">
          <VideoItem video={leftVideo} mb={mb} />
          <div className="phonics-vocab-multi-words">
            <WordRows lines={leftLines} playingKey={playingKey} onPlay={playAudio} prefix="l" />
          </div>
        </div>

        <div className="phonics-vocab-multi-item">
          <VideoItem video={rightVideo} mb={mb} />
          <div className="phonics-vocab-multi-words">
            <WordRows lines={rightLines} playingKey={playingKey} onPlay={playAudio} prefix="r" />
          </div>
        </div>
      </div>
    </Box>
  );
}

/** vocab-stack-story-right: 左2 vocab 上下叠 / 右 story 放大 */
function VocabStackStoryRight({ data, title, mb, audio }) {
  const [playingKey, setPlayingKey] = useState(null);

  const playAudio = (key, src) => {
    audio.play(src, () => setPlayingKey(null));
    setPlayingKey(key);
  };

  const { videos, lines, splitAt } = data;

  // splitAt 决定：前 N 个视频 = vocab（有各自的 video.lines）
  const sa = splitAt !== undefined ? splitAt : 2;
  const vocabVideos = videos.filter((_, i) => i < sa);
  const storyVideo = videos.find((v, i) => i >= sa);

  // story 视频下方的句子：优先用 data.storyLines（独立字段），其次用 video.lines，否则取 lines[sa..]
  const storyLines = data.storyLines || storyVideo?.lines || lines.slice(sa);

  return (
    <Box sx={{ width: '100%' }}>
      <InstructionTitle
        title={title || data.label}
        titleCn="跟着说"
        instructionAudio={data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`}
        inlineCn
      />

      <div className="phonics-vocab-p8-layout">
        {/* 左边：2个词汇视频上下排列 */}
        <div className="phonics-vocab-p8-left">
          {vocabVideos.slice(0, 2).map((v, idx) => (
            <div className="phonics-vocab-multi-item" key={idx}>
              <VideoItem video={v} mb={mb} />
              <div className="phonics-vocab-multi-words">
                <WordRows
                  lines={v.lines || []}
                  playingKey={playingKey}
                  onPlay={playAudio}
                  prefix={`v${idx}w`}
                />
              </div>
            </div>
          ))}
        </div>

        {/* 右边：故事视频（放大） */}
        <div className="phonics-vocab-p8-right">
          <div className="phonics-vocab-multi-item phonics-vocab-p8-story">
            <VideoItem video={storyVideo} mb={mb} />
            {storyLines.length > 0 && (
              <div className="phonics-vocab-multi-sentences">
                <SentenceRows
                  lines={storyLines}
                  playingKey={playingKey}
                  onPlay={playAudio}
                  prefix="s"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Box>
  );
}

/** vocab-story-swap: 等宽互换：左 vocab / 右 story */
function VocabStorySwap({ data, title, mb, audio }) {
  const [playingKey, setPlayingKey] = useState(null);

  const playAudio = (key, src) => {
    audio.play(src, () => setPlayingKey(null));
    setPlayingKey(key);
  };

  const { videos, lines, splitAt } = data;

  // splitAt 决定：前 N 个视频 = vocab，后面的 = story
  const sa = splitAt !== undefined ? splitAt : 1;
  const vocabVideo = videos.find((_, i) => i < sa);
  const storyVideo = videos.find((_, i) => i >= sa);

  // story lines: 优先用 data.storyLines（独立字段），其次用 video.lines，否则取 lines[sa..]
  const storyLines = data.storyLines || storyVideo?.lines || lines.slice(sa);

  return (
    <Box sx={{ width: '100%' }}>
      <InstructionTitle
        title={title || data.label}
        titleCn="跟着说"
        instructionAudio={data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`}
        inlineCn
      />

      <div className="phonics-vocab-p9-layout">
        {/* 左边：词汇视频 + 单词 */}
        <div className="phonics-vocab-multi-item">
          <VideoItem video={vocabVideo} mb={mb} />
          <div className="phonics-vocab-multi-words">
            <WordRows lines={vocabVideo?.lines || []} playingKey={playingKey} onPlay={playAudio} prefix="vw" />
          </div>
        </div>

        {/* 右边：故事视频 + 句子 */}
        <div className="phonics-vocab-multi-item">
          <VideoItem video={storyVideo} mb={mb} />
          {storyLines.length > 0 && (
            <div className="phonics-vocab-multi-sentences">
              <SentenceRows lines={storyLines} playingKey={playingKey} onPlay={playAudio} prefix="s" />
            </div>
          )}
        </div>
      </div>
    </Box>
  );
}

/** story-video-left-lines-right: 左视频+前N行 / 右剩余行 */
function StoryVideoLeftLinesRight({ data, title, mb, audio }) {
  const [playingKey, setPlayingKey] = useState(null);

  const playAudio = (key, src) => {
    audio.play(src, () => setPlayingKey(null));
    setPlayingKey(key);
  };

  const { videos, lines, splitAt } = data;

  // splitAt 决定：
  //   lines[0..splitAt-1] = 左视频下方
  //   lines[splitAt..] = 右边
  const sa = splitAt !== undefined ? splitAt : 3;
  const leftLines = videos[0]?.lines || lines.slice(0, sa);
  const rightLines = lines.slice(sa);

  // 判断是否有中文，据此选择 LinesBi 或 LinesMono
  const hasCn = lines.some(l => l.cn);
  const LinesComp = hasCn ? LinesBi : LinesMono;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, gap: 0.5 }}>
      <InstructionTitle
        title={title || data.label}
        titleCn={data.titleCn || "跟着说"}
        instructionAudio={data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`}
        inlineCn
      />
      <div className="phonics-lt-story">
        <div className="phonics-lt-left">
          <VideoItem video={videos[0]} mb={mb} />
          <div className="phonics-lt-left-lines">
            <LinesComp lines={leftLines} playingKey={playingKey} onPlay={playAudio} prefix="l" />
          </div>
        </div>

        <div className="phonics-lt-right">
          <LinesComp lines={rightLines} playingKey={playingKey} onPlay={playAudio} prefix="r" />
        </div>
      </div>
    </Box>
  );
}

/** text-left-words-right-phrases: 无视频，纯文字左右分栏（左侧单词，右侧词组） */
function TextLeftWordsRightPhrases({ data, title, mb, audio }) {
  const [playingKey, setPlayingKey] = useState(null);

  const playAudio = (key, src) => {
    audio.play(src, () => setPlayingKey(null));
    setPlayingKey(key);
  };

  const { lines, splitAt } = data;
  const sa = splitAt !== undefined ? splitAt : 4;
  const leftLines = lines.slice(0, sa);
  const rightLines = lines.slice(sa);

  return (
    <Box sx={{ width: '100%' }}>
      <InstructionTitle
        title={title || data.label}
        titleCn="跟着说"
        instructionAudio={data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`}
        inlineCn
      />

      <div className="phonics-vocab-p8-layout">
        {/* 左侧：单词 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '50%' }}>
          <WordRows lines={leftLines} playingKey={playingKey} onPlay={playAudio} prefix="w" />
        </div>

        {/* 右侧：词组 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '50%' }}>
          <WordRows lines={rightLines} playingKey={playingKey} onPlay={playAudio} prefix="ph" />
        </div>
      </div>
    </Box>
  );
}

/** ======================= vocab-dual 组件 ======================= */
/** 左右各一个词汇组：图在上，图下单词+句子并排 */
function VocabDual({ data, title, mb, audio }) {
  const [playingKey, setPlayingKey] = useState(null);

  const playAudio = (key, src) => {
    audio.play(src, () => setPlayingKey(null));
    setPlayingKey(key);
  };

  const { videos, lines = [] } = data;
  const numItems = videos.length || 2;

  // 每组 lines 数量 = 总 lines / item 数
  const groupSize = Math.ceil(lines.length / numItems);

  // 按 groupSize 切分左右两侧
  const leftLines = lines.slice(0, groupSize);
  const rightLines = lines.slice(groupSize);

  const leftVideo = videos[0] || {};
  const rightVideo = videos[1] || {};

  // 每组内：第1条是单词，第2条是句子
  const splitWordAt = 1;
  const leftWordLines = leftLines.slice(0, splitWordAt);
  const leftSentenceLines = leftLines.slice(splitWordAt);
  const rightWordLines = rightLines.slice(0, splitWordAt);
  const rightSentenceLines = rightLines.slice(splitWordAt);

  return (
    <Box sx={{ width: '100%' }}>
      <InstructionTitle
        title={title || data.label}
        titleCn="跟着说"
        instructionAudio={data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`}
        inlineCn
      />

      <div className="phonics-vocab-multi">
        {/* 左侧 */}
        <div className="phonics-vocab-multi-item">
          <VideoItem video={leftVideo} mb={mb} />
          <div className="phonics-vocab-multi-words">
            <WordRows lines={leftWordLines} playingKey={playingKey} onPlay={playAudio} prefix="wl" />
            {leftSentenceLines.length > 0 && (
              <div className="phonics-vocab-sentences">
                <SentenceRows lines={leftSentenceLines} playingKey={playingKey} onPlay={playAudio} prefix="sl" />
              </div>
            )}
          </div>
        </div>

        {/* 右侧 */}
        <div className="phonics-vocab-multi-item">
          <VideoItem video={rightVideo} mb={mb} />
          <div className="phonics-vocab-multi-words">
            <WordRows lines={rightWordLines} playingKey={playingKey} onPlay={playAudio} prefix="wr" />
            {rightSentenceLines.length > 0 && (
              <div className="phonics-vocab-sentences">
                <SentenceRows lines={rightSentenceLines} playingKey={playingKey} onPlay={playAudio} prefix="sr" />
              </div>
            )}
          </div>
        </div>
      </div>
    </Box>
  );
}

/** ======================= vocab-left-text-right 组件 ======================= */
/** 左栏图片堆叠，右栏文字（单词+句子） */
function VocabLeftTextRight({ data, title, mb, audio }) {
  const [playingKey, setPlayingKey] = useState(null);

  const playAudio = (key, src) => {
    audio.play(src, () => setPlayingKey(null));
    setPlayingKey(key);
  };

  const { videos = [], lines = [] } = data;

  // 每张图对应一组文字：[单词, 句子]
  const groupSize = Math.ceil(lines.length / videos.length);

  return (
    <Box sx={{ width: '100%' }}>
      <InstructionTitle
        title={title || data.label}
        titleCn="跟着说"
        instructionAudio={data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`}
        inlineCn
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {videos.map((video, i) => {
          const groupLines = lines.slice(i * groupSize, (i + 1) * groupSize);
          return (
            <div key={i} style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
              {/* 左：图片 */}
              <div style={{ width: '50%', maxWidth: 300 }}>
                <VideoItem video={video} mb={mb} />
              </div>

              {/* 右：文字 */}
              <div style={{ width: '50%', display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
                {groupLines.map((line, j) => {
                  const isWord = j === 0;
                  const key = `r${i}-${j}`;
                  return (
                    <div key={j} className="phonics-vocab-word-row">
                      <span className={isWord ? 'phonics-vocab-word-en' : 'phonics-vocab-sentence-en'}>
                        {line.en}
                      </span>
                      <MiniBtn
                        playing={playingKey === key}
                        onClick={() => playAudio(key, line.audioEn)}
                        title={line.en}
                      />
                      {line.cn && (
                        <>
                          <span className={isWord ? 'phonics-vocab-word-cn' : 'phonics-vocab-sentence-cn'}>
                            {line.cn}
                          </span>
                          <MiniBtn
                            playing={playingKey === `${key}-cn`}
                            onClick={() => playAudio(`${key}-cn`, line.audioCn)}
                            title={line.cn}
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Box>
  );
}

/** video-left-vocab-right-lines-bottom: 左上视频 + 右上单词 + 下部全宽台词 */
function VideoLeftVocabRightLinesBottom({ data, title, mb, audio }) {
  const [playingKey, setPlayingKey] = useState(null);

  const playAudio = (key, src) => {
    audio.play(src, () => setPlayingKey(null));
    setPlayingKey(key);
  };

  const { videos, lines, splitAt } = data;
  const sa = splitAt !== undefined ? splitAt : 2;
  // videoWidth: 视频容器宽度百分比，默认 50
  const videoWidth = data.videoWidth !== undefined ? data.videoWidth : 50;
  const wordWidth = 100 - videoWidth;

  // lines[0..splitAt-1] = 右上单词；lines[splitAt..] = 下部全宽台词
  const wordLines = lines.slice(0, sa);
  const bottomLines = lines.slice(sa);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, gap: 1 }}>
      <InstructionTitle
        title={title || data.label}
        titleCn="跟着说"
        instructionAudio={data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`}
        inlineCn
      />

      {/* 上方：左上视频 + 右上单词 */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* 左上：视频 */}
        <div style={{ width: `${videoWidth}%` }}>
          <VideoItem video={videos[0]} mb={mb} />
        </div>
        {/* 右上：单词 */}
        <div style={{ width: `${wordWidth}%`, paddingTop: 8 }}>
          <WordRows lines={wordLines} playingKey={playingKey} onPlay={playAudio} prefix="w" skipTopMargin />
        </div>
      </div>

      {/* 下部全宽台词 */}
      <div className="phonics-vocab-sentences" style={{ width: '100%' }}>
        <SentenceRows lines={bottomLines} playingKey={playingKey} onPlay={playAudio} prefix="s" />
      </div>
    </Box>
  );
}

/** ======================= single-image 组件 ======================= */
/** 单图展示：图片居中，无文字（用于 kiss 等无文字的词汇页） */
function SingleImage({ data, title, mb, audio }) {
  const { videos } = data;
  const mainVideo = videos?.[0] || {};

  return (
    <Box sx={{ width: '100%' }}>
      <InstructionTitle
        title={title || data.label}
        titleCn="跟着说"
        instructionAudio={data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`}
        inlineCn
      />

      <div className="phonics-story-video-center" style={{ maxWidth: 400 }}>
        <VideoItem video={mainVideo} mb={mb} />
      </div>
    </Box>
  );
}

/** ======================= text-repeat-table 组件 ======================= */
/** 纯文字跟读练习表：类似 PPT 语法表格，左侧英文，右侧翻译（可选），每行有播放按钮 */
function TextRepeatTable({ data, title, mb, audio }) {
  const [playingKey, setPlayingKey] = useState(null);
  const [instrPlaying, setInstrPlaying] = useState(false);

  const playAudio = (key, src) => {
    audio.play(src, () => setPlayingKey(null));
    setPlayingKey(key);
  };

  const { headerTitle, groups = [] } = data;
  // groups: [{ title, rows: [{ en, cn?, audioEn }] }]

  return (
    <Box sx={{ width: '100%', px: 2 }}>
      {headerTitle && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 26, fontWeight: 700, color: '#111' }}>{headerTitle}</span>
          <span style={{ fontSize: 20, fontWeight: 600, color: '#FF9F1C' }}>跟着说</span>
          <MiniBtn
            playing={instrPlaying}
            onClick={() => { audio.play(data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`, () => setInstrPlaying(false)); setInstrPlaying(true); }}
            title="跟着说"
          />
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {groups.map((group, gi) => (
          <div key={gi}>
            {group.title && (
              <div style={{ fontSize: 22, fontWeight: 700, color: '#333', marginBottom: 8 }}>
                {group.title}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {group.rows.map((row, ri) => {
                const key = `g${gi}-r${ri}`;
                return (
                  <div
                    key={ri}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <span style={{ fontSize: 26, color: '#111', lineHeight: 1.5 }}>
                      {row.en}
                    </span>
                    <MiniBtn
                      playing={playingKey === key}
                      onClick={() => playAudio(key, row.audioEn)}
                      title={row.en}
                    />
                    {row.cn && (
                      <>
                        <span style={{ fontSize: 20, color: '#888', minWidth: 140 }}>
                          {row.cn}
                        </span>
                        {row.audioCn && (
                          <MiniBtn
                            playing={playingKey === `${key}-cn`}
                            onClick={() => playAudio(`${key}-cn`, row.audioCn)}
                            title={row.cn}
                          />
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Box>
  );
}

/** number-grid: 数字学习页面，展示多个数字图片 */
function NumberGrid({ data, title, mb, audio }) {
  const { images = [], headerTitle } = data;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
      <InstructionTitle
        title={title || data.label}
        titleCn={headerTitle || '数一数'}
        inlineCn
      />

      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(images.length, 5)}, 1fr)`,
        gap: 16,
        padding: '8px 16px',
        flex: 1,
        alignItems: 'center',
      }}>
        {images.map((img, idx) => (
          <div key={idx} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
          }}>
            <img
              src={img.src}
              alt={`${img.number}`}
              style={{
                width: '100%',
                maxWidth: 200,
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            />
            <div style={{
              fontSize: 32,
              fontWeight: 800,
              color: '#FF6B35',
              lineHeight: 1,
            }}>
              {img.number}
            </div>
            <div style={{
              fontSize: 18,
              color: '#555',
              fontWeight: 600,
            }}>
              {img.word}
            </div>
          </div>
        ))}
      </div>
    </Box>
  );
}

/** number-grid-2row: 数字两行布局（奇数行在上，偶数行在下） */
function NumberGrid2Row({ data, title, mb, audio }) {
  const { rows = [], headerTitle } = data;
  const [playingKey, setPlayingKey] = useState(null);

  const playAudio = (key, src) => {
    if (!src) return;
    audio.play(src, () => setPlayingKey(null));
    setPlayingKey(key);
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, gap: 2 }}>
      <InstructionTitle
        title={title || data.label}
        titleCn={headerTitle || '跟着说'}
        instructionAudio={data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`}
        inlineCn
      />

      {rows.map((row, rowIdx) => (
        <div
          key={rowIdx}
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 48,
          }}
        >
          {row.map((img, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                cursor: img.audio ? 'pointer' : 'default',
              }}
              onClick={() => img.audio && playAudio(`r${rowIdx}c${idx}`, img.audio)}
            >
              <img
                src={img.src}
                alt={img.word}
                style={{
                  height: 130,
                  width: 'auto',
                  borderRadius: 10,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 18, color: '#555', fontWeight: 600 }}>
                  {img.word}
                </span>
                {img.audio && (
                  <MiniBtn
                    playing={playingKey === `r${rowIdx}c${idx}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      playAudio(`r${rowIdx}c${idx}`, img.audio);
                    }}
                    title={img.word}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      ))}
    </Box>
  );
}

/** story-left-vocab-right: 左视频 + 视频下方句子 / 右全量词汇（等宽左右） */
function StoryLeftVocabRight({ data, title, mb, audio }) {
  const [playingKey, setPlayingKey] = useState(null);

  const playAudio = (key, src) => {
    audio.play(src, () => setPlayingKey(null));
    setPlayingKey(key);
  };

  const { videos, lines, splitAt } = data;
  const mainVideo = videos[0] || {};

  // splitAt 决定：lines[0..splitAt-1] = 左（视频下方），lines[splitAt..] = 右侧词汇
  const sa = splitAt !== undefined ? splitAt : 1;
  const leftLines = lines.slice(0, sa);
  const rightLines = lines.slice(sa);

  return (
    <Box sx={{ width: '100%' }}>
      <InstructionTitle
        title={title || data.label}
        titleCn="跟着说"
        instructionAudio={data.instructionAudio || `${mb}/audio_instruction_repeat.mp3`}
        inlineCn
      />

      <div className="phonics-vocab-p8-layout">
        {/* 左侧：视频 + 句子 */}
        <div className="phonics-vocab-left">
          <VideoItem video={mainVideo} mb={mb} />
          {leftLines.length > 0 && (
            <div className="phonics-vocab-sentences">
              <SentenceRows lines={leftLines} playingKey={playingKey} onPlay={playAudio} prefix="sl" />
            </div>
          )}
        </div>

        {/* 右侧：词汇 */}
        <div className="phonics-vocab-content">
          <WordRows lines={rightLines} playingKey={playingKey} onPlay={playAudio} prefix="w" />
        </div>
      </div>
    </Box>
  );
}

/** ======================= 主组件 ======================= */

function PhonicsStory({ data, title, mediaBase: mb, layout }) {
  const audio = useAudio();

  React.useEffect(() => {
    return () => audio.stop();
  }, []);

  const actualLayout = data?.layout || layout || 'video-top-lines-bottom';

  switch (actualLayout) {
    case 'video-top-lines-bottom':
      return <VideoTopLinesBottom data={data} title={title} mb={mb} audio={audio} />;
    case 'video-left-vocab-right':
      return <VideoLeftVocabRight data={data} title={title} mb={mb} audio={audio} />;
    case 'dual-videos':
      return <DualVideos data={data} title={title} mb={mb} audio={audio} />;
    case 'vocab-stack-story-right':
      return <VocabStackStoryRight data={data} title={title} mb={mb} audio={audio} />;
    case 'vocab-story-swap':
      return <VocabStorySwap data={data} title={title} mb={mb} audio={audio} />;
    case 'story-video-left-lines-right':
      return <StoryVideoLeftLinesRight data={data} title={title} mb={mb} audio={audio} />;
    case 'text-left-words-right-phrases':
      return <TextLeftWordsRightPhrases data={data} title={title} mb={mb} audio={audio} />;
    case 'video-left-vocab-right-lines-bottom':
      return <VideoLeftVocabRightLinesBottom data={data} title={title} mb={mb} audio={audio} />;
    case 'vocab-dual':
      return <VocabDual data={data} title={title} mb={mb} audio={audio} />;
    case 'vocab-left-text-right':
      return <VocabLeftTextRight data={data} title={title} mb={mb} audio={audio} />;
    case 'text-repeat-table':
      return <TextRepeatTable data={data} title={title} mb={mb} audio={audio} />;
    case 'single-image':
      return <SingleImage data={data} title={title} mb={mb} audio={audio} />;
    case 'number-grid':
      return <NumberGrid data={data} title={title} mb={mb} audio={audio} />;
    case 'number-grid-2row':
      return <NumberGrid2Row data={data} title={title} mb={mb} audio={audio} />;
    default:
      return <VideoTopLinesBottom data={data} title={title} mb={mb} audio={audio} />;
  }
}

export default PhonicsStory;
